import { MediaModel } from 'src/app/common/media.model';
import { WalletTemplateField } from 'src/app/common/wallet-template-field.model';
import { WalletAppleField } from 'src/app/common/wallet-template-enums';

export type SaveValidationFieldKey = 'appleBarcodeText' | 'appleSecondaryOrAuxiliary' | 'appleImages';

export interface SaveValidationError {
  tabIndex: number;
  fieldKey: SaveValidationFieldKey;
  messageKey: string;
}

export interface AppleWalletSaveValidationContext {
  appleWalletFields: WalletTemplateField[];
  mediaModel: MediaModel;
  tabIndexContent: number;
  tabIndexAppearance: number;
  normalizeFields: (fields: WalletTemplateField[]) => WalletTemplateField[];
  normalizeFieldOrder: (value: any) => number;
}

export function validateAppleWalletForSave(context: AppleWalletSaveValidationContext): SaveValidationError[] {
  const errors: SaveValidationError[] = [];

  if (!hasAppleBarcodeText(context)) {
    errors.push({
      tabIndex: context.tabIndexContent,
      fieldKey: 'appleBarcodeText',
      messageKey: 'admin.web.wallet-validation-apple-barcode-text-required',
    });
  }

  if (!hasAppleSecondaryOrAuxiliaryField(context)) {
    errors.push({
      tabIndex: context.tabIndexContent,
      fieldKey: 'appleSecondaryOrAuxiliary',
      messageKey: 'admin.web.wallet-validation-apple-secondary-or-auxiliary-require',
    });
  }

  if (!hasAppleRequiredImages(context)) {
    errors.push({
      tabIndex: context.tabIndexAppearance,
      fieldKey: 'appleImages',
      messageKey: 'admin.web.wallet-validation-apple-images-required',
    });
  }

  return errors;
}

export function isAppleValidationFieldSatisfied(
  fieldKey: SaveValidationFieldKey,
  context: AppleWalletSaveValidationContext,
): boolean {
  switch (fieldKey) {
    case 'appleBarcodeText':
      return hasAppleBarcodeText(context);
    case 'appleSecondaryOrAuxiliary':
      return hasAppleSecondaryOrAuxiliaryField(context);
    case 'appleImages':
      return hasAppleRequiredImages(context);
    default:
      return true;
  }
}

function hasAppleBarcodeText(context: AppleWalletSaveValidationContext): boolean {
  const normalized = context.normalizeFields(context.appleWalletFields ?? []);
  const barcodeField = normalized.find(f =>
    f.appleField === WalletAppleField.BarcodeValue
  );

  if(!barcodeField) {
    return false;
  }

  return true;
}

function hasAppleSecondaryOrAuxiliaryField(context: AppleWalletSaveValidationContext): boolean {
  const normalized = context.normalizeFields(context.appleWalletFields ?? []);

  return normalized.some(f => {
    if (f.appleField !== WalletAppleField.Secondary && f.appleField !== WalletAppleField.Auxiliary) {
      return false;
    }

    return true;
  });
}

function hasAppleRequiredImages(context: AppleWalletSaveValidationContext): boolean {
  return !!context.mediaModel?.walletLogoUID && !!context.mediaModel?.walletImageUID;
}
